import { useCallback, useState } from 'react';

import { AccessSummaryTypes, NativeResources } from '@ariksa/inventory-core';
import { each, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { QueryStatus } from 'services/types';
import { VulnerabilitySeverity } from 'services/Vulnerability/types';

import { InventoryService } from 'api/services';
import { getPublishedTime } from 'components/DataEntry';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { ActiveResourceInfo } from 'containers/ActiveCloudResource/types';

export function useActiveResourceActions() {
  const dispatch = useDispatch();
  const {
    resourceUuid,
    resourceType,
    resourceAccountId,
    resourceId,
    cloudProvider,
    resourceInsight,
    activeTabIndex,
    activeTabName,
    vulnerabilityPublishedTime,
    showResolvedVulnerabilities,
    isPatchable,
    vulnerabilitySeverity,
    alerts,
    amiHosts,
    resourceArn,
  } = useSelector(selectActiveResource);
  const { isOpenMetadata, pushMetadataHistory } = useActiveResourceContext();
  const { hosts } = amiHosts;

  const [params, setParams] = useState<ActiveResourceInfo>({
    uuid: resourceUuid,
    resourceType: resourceType as NativeResources,
    accountId: resourceAccountId,
    resourceId: resourceId,
    cloudProvider,
  });

  // useEffect(() => {
  //   setParams({ resourceUuid, resourceType, resourceAccountId });
  // }, [resourceUuid, resourceType, resourceAccountId]);

  const getTickets = useCallback(() => {
    dispatch(actions.getTickets({}));
  }, [dispatch]);

  const updateActiveResource = useCallback(
    (resourceInfo: ActiveResourceInfo) => {
      const resourceChanged =
        resourceInfo.resourceType !== resourceType ||
        resourceInfo.uuid !== resourceUuid ||
        resourceInfo.accountId !== resourceAccountId ||
        resourceInfo.resourceId !== resourceId;

      if (isOpenMetadata && resourceChanged) {
        pushMetadataHistory({
          resource: {
            resourceType,
            uuid: resourceUuid,
            accountId: resourceAccountId,
            resourceId,
            resourceArn,
            cloudProvider,
          },
          activeTabIndex,
          activeTabName,
        });
      }

      setParams(resourceInfo);
      dispatch(actions.updateResource(resourceInfo));
    },
    [
      activeTabIndex,
      activeTabName,
      cloudProvider,
      dispatch,
      isOpenMetadata,
      pushMetadataHistory,
      resourceAccountId,
      resourceArn,
      resourceId,
      resourceType,
      resourceUuid,
    ],
  );

  const isNoOp = useCallback(() => {
    return resourceUuid === '';
  }, [resourceUuid]);

  const getResourceDetails = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getResourceDetails({
        q: {
          resourceUuid,
          resourceType: resourceType as any,
          account: resourceAccountId,
        },
      }),
    );
  }, [dispatch, isNoOp, resourceAccountId, resourceType, resourceUuid]);

  const getAMIHosts = useCallback(() => {
    if (!resourceId) return;
    dispatch(
      actions.getAMIHosts({
        q: {
          vMByAMIRequest: {
            account_id: !resourceAccountId ? [resourceAccountId] : [],
            ami: resourceId,
            page: hosts.page.info.page_number,
            size: hosts.page.info.page_size,
          },
        },
        onSuccess: res => {
          dispatch(
            actions.getAMIHostsRiskContext({
              q: {
                riskContextRequest: { uuids: map(res?.items, o => o?.uuid) },
              },
            }),
          );

          const resources: Record<string, Record<string, any>> = {};
          each(res?.items, o =>
            !resources[o.native_name]
              ? (resources[o.native_name] = { [o.uuid]: o })
              : (resources[o.native_name] = {
                  ...resources?.[o.native_name],
                  [o.uuid]: o,
                }),
          );
          if (!isEmpty(resources)) {
            each(resources, (o, key) => {
              const uuids = map(o, u => u?.uuid);
              const accounts = map(o, u => u?.account);
              const payload = {
                resourceVulnerabilitiesRequest: {
                  native_resource: key as NativeResources,
                  resource_ids: uuids,
                  account_ids: accounts,
                },
              };
              dispatch(
                actions.getAMIHostsVulnerabilities({
                  q: payload,
                  status: QueryStatus.pending,
                }),
              );
              InventoryService.Vulnerability.getVulnerabilities(payload).then(
                res => {
                  dispatch(
                    actions.getAMIHostsVulnerabilities({
                      q: payload,
                      status: QueryStatus.fulfilled,
                      data: res.data,
                    }),
                  );
                },
              );
            });
          }
        },
      }),
    );
  }, [hosts.page.info, dispatch, resourceAccountId, resourceId]);

  const getDynamicResourceDetails = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getDynamicResourceDetails({
        q: {
          uuid: resourceUuid,
          resourceType: resourceType as any,
          accountId: resourceAccountId,
        },
      }),
    );
  }, [dispatch, isNoOp, resourceAccountId, resourceType, resourceUuid]);

  const getResourceRiskContext = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getRiskContext({
        q: {
          riskContextRequest: {
            uuids: [resourceUuid],
          },
        },
      }),
    );
  }, [dispatch, isNoOp, resourceUuid]);

  const getPermissions = useCallback(() => {
    if (isNoOp()) return;
    !!resourceInsight.data?.arn &&
      dispatch(
        actions.getPermissions({
          q: {
            resourceUuid: resourceUuid,
            accountId: resourceAccountId,
            region: resourceInsight.data?.region,
          },
        }),
      );
  }, [dispatch, isNoOp, resourceAccountId, resourceInsight.data, resourceUuid]);

  const getResourceInsight = useCallback(() => {
    //if (!resourceUuid) return;
    dispatch(
      actions.getResourceInsight({
        q: {
          insightV2Request: {
            resource_type: resourceType as NativeResources,
            resource_uuids: !!resourceUuid ? [resourceUuid] : [],
            resource_ids: !!resourceId ? [resourceId] : [],
            arns: !!resourceArn ? [resourceArn] : [],
            account_id: [resourceAccountId],
          },
        },
      }),
    );
  }, [
    dispatch,
    resourceUuid,
    resourceType,
    resourceAccountId,
    resourceId,
    resourceArn,
  ]);

  const getNetworkMap = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getNetworkMap({
        q: {
          nativeResource: resourceType,
          searchQuery: { uuid: resourceUuid, account_ids: [resourceAccountId] },
        },
      }),
    );
  }, [dispatch, isNoOp, resourceAccountId, resourceType, resourceUuid]);

  const getEntityTimeline = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getEntityTimeline({
        q: { accountId: resourceAccountId, entityUuid: resourceUuid },
      }),
    );
  }, [dispatch, isNoOp, resourceAccountId, resourceUuid]);

  const getEntitySeverity = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getEntitySeverity({
        q: { requestBody: [resourceUuid] },
      }),
    );
  }, [dispatch, resourceUuid, isNoOp]);

  // cache pageInfo to avoid loops
  const getAlerts = useCallback(
    blueprintId => {
      if (isNoOp()) return;
      dispatch(
        actions.getAlerts({
          q: {
            entityUuid: params.uuid,
            resource: params.resourceType,
            pageSize: alerts.page.info.page_size,
            pageNumber: alerts.page.info.page_number,
            blueprintId,
          },
          onSuccess: res => {
            !isEmpty(res) &&
              dispatch(
                actions.getAlertStatus({
                  q: { alertId: map(res?.results, o => o?.uuid!) },
                }),
              );
          },
        }),
      );
    },
    [dispatch, isNoOp, params, alerts.page.info],
  );

  const getAlertBlueprints = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getAlertBlueprints({
        q: {
          resourceId: resourceUuid,
          accountId: resourceAccountId,
        },
      }),
    );
  }, [dispatch, isNoOp, resourceUuid, resourceAccountId]);

  const getVulnerabilitySummary = useCallback(() => {
    if (
      resourceType !== NativeResources.Ec2Instance &&
      resourceType !== NativeResources.LambdaFunction &&
      resourceType !== NativeResources.Container &&
      resourceType !== NativeResources.AmazonMachineImage &&
      resourceType !== NativeResources.GcpComputeEngine &&
      resourceType !== NativeResources.GcpCloudFunctions &&
      resourceType !== NativeResources.AzureVirtualMachine &&
      resourceType !== NativeResources.AzureVirtualMachineScaleSetInstance &&
      resourceType !== NativeResources.AzureVirtualMachineScaleSets &&
      resourceType !== NativeResources.AzureFunctions &&
      resourceType !== NativeResources.AzureContainer &&
      resourceType !== NativeResources.GcpContainer
    )
      return;

    let id: Record<string, any> = {
      instanceId: encodeURIComponent(resourceId),
    };

    if (
      resourceType === NativeResources.Container ||
      resourceType === NativeResources.AzureContainer ||
      resourceType === NativeResources.GcpContainer
    ) {
      id = { containerId: encodeURIComponent(resourceId) };
    }

    const published = getPublishedTime(vulnerabilityPublishedTime.value);
    const publishedTime = showResolvedVulnerabilities
      ? { resolvedAt: published }
      : { publishedAt: published };
    const patchable =
      isPatchable === 'yes' ? true : isPatchable === 'no' ? false : undefined;
    const severityFilter = !!vulnerabilitySeverity
      ? { severity: vulnerabilitySeverity as VulnerabilitySeverity }
      : undefined;
    const payload = {
      resolved: showResolvedVulnerabilities,
      isPatchable: patchable,
      ...publishedTime,
      ...severityFilter,
    };
    if (resourceType === NativeResources.AmazonMachineImage) {
      !!resourceUuid &&
        dispatch(
          actions.getAMIVulnerabilitySummary({
            q: {
              resourceId: resourceUuid,
              nativeResource: NativeResources.AmazonMachineImage,
              ...payload,
            },
          }),
        );
    } else {
      !!resourceId &&
        dispatch(
          actions.getVulnerabilitySummary({
            q: {
              ...id,
              ...payload,
            },
          }),
        );
    }
  }, [
    dispatch,
    resourceType,
    resourceId,
    vulnerabilityPublishedTime,
    showResolvedVulnerabilities,
    isPatchable,
    vulnerabilitySeverity,
    resourceUuid,
  ]);

  const getCanAccessSummary = useCallback(
    (summaryType: AccessSummaryTypes) => {
      if (isNoOp()) return;
      dispatch(
        actions.getCanAccessSummary({
          q: {
            accountId: resourceAccountId,
            uuid: resourceUuid,
            nativeResourceType: resourceType,
            summaryType,
          },
        }),
      );
    },
    [dispatch, isNoOp, resourceAccountId, resourceUuid, resourceType],
  );

  const getPrivilegeSummary = useCallback(() => {
    if (isNoOp()) return;
    dispatch(
      actions.getPrivilegeSummary({
        q: {
          accountId: resourceAccountId,
          uuid: resourceUuid,
          nativeResourceType: resourceType,
        },
      }),
    );
  }, [dispatch, isNoOp, resourceAccountId, resourceUuid, resourceType]);

  const getAccessMapData = useCallback(
    (expanded: boolean = false) => {
      if (isNoOp()) return;

      const resourcesWithAccessMap: NativeResources[] = [
        NativeResources.AccountRoot,
        NativeResources.IamUser,
        NativeResources.IamGroup,
        NativeResources.IamRole,
        NativeResources.IamPolicy,
        NativeResources.Ec2Instance,
        NativeResources.ElasticContainerRegistry,
        NativeResources.ElasticKubernetesService,
        NativeResources.ElasticContainerService,
        NativeResources.EcsCluster,
        NativeResources.OktaUser,
        NativeResources.AzureAdUser,
        NativeResources.SnowflakeUsers,
        NativeResources.SnowflakeTables,
        NativeResources.SnowflakeDatabases,
        NativeResources.SnowflakeRoles,
        NativeResources.SnowflakeSchemas,
      ];

      //console.log(resourceType, resourcesWithAccessMap.includes(resourceType));

      if (resourcesWithAccessMap.includes(resourceType)) {
        resourceAccountId &&
          dispatch(
            actions.getAccessMapData({
              q: {
                accountId: resourceAccountId,
                uuid: resourceUuid,
                nativeResourceType: resourceType,
                expanded: expanded,
              },
            }),
          );
      }
    },
    [dispatch, isNoOp, resourceAccountId, resourceUuid, resourceType],
  );

  return {
    updateActiveResource,
    getResourceDetails,
    getResourceInsight,
    getAlerts,
    getEntityTimeline,
    getVulnerabilitySummary,
    getEntitySeverity,
    getCanAccessSummary,
    getAccessMapData,
    getPermissions,
    getNetworkMap,
    getPrivilegeSummary,
    getDynamicResourceDetails,
    getResourceRiskContext,
    getAMIHosts,
    getAlertBlueprints,
  };
}
